import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  campaignBlockers, channelName, inboxApi, istInputToUtcIso, istInputValue, parseVariableMap, sameVariableMap,
  templateVariablePositions,
  type CampaignDetail, type ContactField, type Segment, type Template, type VariableBinding,
} from '@wa/shared';

/**
 * Template, audience, values, then send.
 *
 * Four steps in one scroll rather than a wizard: every one of them changes what the
 * others mean — swapping the template changes how many values there are, changing the
 * audience changes the preview and the price — and a wizard that hides the earlier
 * steps makes that invisible.
 */
/**
 * `locked` is the broadcast screen's way in: the template was chosen in its
 * picker and the list is the one whose thread is open, so neither is a choice
 * here — they are shown, not offered. What is left is the reference's own
 * flow: values, when, send.
 */
export function CampaignBuilder({ campaign, onClose, locked }: {
  campaign: CampaignDetail; onClose: () => void; locked?: { listName: string };
}) {
  const queryClient = useQueryClient();

  const [name, setName] = useState(campaign.name);
  const [templateId, setTemplateId] = useState(campaign.templateId);
  const [segmentId, setSegmentId] = useState<number | null>(campaign.segmentId);
  // The number it goes out from. Chosen for the draft as the first number the
  // agent may work, which is right for a client with one and silently wrong
  // for a client with a marketing number and a transactional one.
  const [channelId, setChannelId] = useState<number>(campaign.channelId);
  const [map, setMap] = useState<VariableBinding[]>(() => parseVariableMap(campaign.variableMap));
  const [scheduled, setScheduled] = useState(false);
  const [when, setWhen] = useState(() => istInputValue(60));
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The template's header. A file is uploaded the moment it is chosen (to Meta,
  // through the same route a chat attachment takes) and only its id is saved with
  // the draft; a text header is a value like any other.
  const [header, setHeader] = useState<{ assetId: number | null; fileName: string | null }>({
    assetId: campaign.headerMediaAssetId ?? null, fileName: campaign.headerFileName ?? null,
  });
  const [headerText, setHeaderText] = useState(campaign.headerText ?? '');
  const [uploading, setUploading] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const templates = useQuery({ queryKey: ['templates'], queryFn: inboxApi.templates });
  const segments = useQuery({ queryKey: ['segments'], queryFn: inboxApi.segments });
  const channels = useQuery({ queryKey: ['channels'], queryFn: inboxApi.channels, staleTime: 300_000 });
  const sendFrom = (channels.data ?? []).filter((c) => c.isActive);
  const fields = useQuery({ queryKey: ['contact-fields'], queryFn: inboxApi.contactFields });

  const template = (templates.data ?? []).find((t) => t.id === templateId);
  const headerFormat = (template?.headerFormat ?? campaign.templateHeaderFormat ?? 'NONE').toUpperCase();
  const needsHeaderMedia = template ? Boolean(template.needsHeaderMedia) : Boolean(campaign.templateNeedsHeaderMedia);
  const needsHeaderText = template ? Boolean(template.needsHeaderText) : Boolean(campaign.templateNeedsHeaderText);

  // How many values the chosen template needs, read from its body rather than
  // trusted from a column: a template re-synced from Meta can change shape, and a
  // map sized to the old one fails once per recipient.
  const needed = useMemo(
    () => (template ? Math.max(templateVariablePositions(template.bodyText).length, 0) : campaign.bodyVariables),
    [template, campaign.bodyVariables],
  );

  // Resize rather than reset. Somebody who has filled three values and swaps to a
  // four-value template should not lose the three.
  useEffect(() => {
    setMap((prev) => {
      if (prev.length === needed) return prev;
      const next = prev.slice(0, needed);
      while (next.length < needed) next.push({ source: 'name', fallback: 'there' });
      return next;
    });
  }, [needed]);

  const save = useMutation({
    mutationFn: () => inboxApi.updateCampaign(campaign.id, {
      name: name.trim(), templateId, channelId, segmentId, variableMap: map,
      // 0 clears a file left over from a template that had a header.
      headerMediaAssetId: needsHeaderMedia ? header.assetId : (campaign.headerMediaAssetId ? 0 : undefined),
      headerText: needsHeaderText ? headerText.trim() : undefined,
    }),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      // Write what was just saved into the cached draft straight away, so "dirty"
      // clears and Send unlocks now — not whenever the refetch lands. The refetch
      // still runs, for anything the server derived (estimate, preview, file name).
      queryClient.setQueryData<{ campaign: CampaignDetail } | undefined>(['campaign', campaign.id], (old) => old && ({
        ...old,
        campaign: {
          ...old.campaign,
          name: name.trim(), templateId, channelId, segmentId, variableMap: JSON.stringify(map),
          headerMediaAssetId: needsHeaderMedia ? header.assetId : null,
          headerFileName: needsHeaderMedia ? header.fileName : null,
          headerText: needsHeaderText ? headerText.trim() : null,
        },
      }));
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not save that.'),
  });

  // The estimate and the preview both depend on what is stored, not on what is on
  // screen, so they are refetched after a save rather than computed here.
  const estimate = useQuery({
    queryKey: ['campaign-estimate', campaign.id],
    queryFn: () => inboxApi.campaignEstimate(campaign.id),
    enabled: Boolean(campaign.segmentId ?? campaign.retargetOfCampaignId),
  });

  const preview = useQuery({
    queryKey: ['campaign-preview', campaign.id],
    queryFn: () => inboxApi.campaignPreview(campaign.id),
    enabled: Boolean(campaign.segmentId ?? campaign.retargetOfCampaignId),
  });

  const launch = useMutation({
    mutationFn: () => inboxApi.launchCampaign(campaign.id, scheduled ? istInputToUtcIso(when) : null),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['campaigns'] });
      void queryClient.invalidateQueries({ queryKey: ['campaign', campaign.id] });
      // The launch put the estimate on hold; the header chip should say so.
      void queryClient.invalidateQueries({ queryKey: ['wallet'] });
      onClose();
    },
    onError: (e) => setError(e instanceof Error ? e.message : 'Could not start that campaign.'),
  });

  const dirty =
    name.trim() !== campaign.name ||
    templateId !== campaign.templateId ||
    channelId !== campaign.channelId ||
    segmentId !== campaign.segmentId ||
    !sameVariableMap(map, parseVariableMap(campaign.variableMap)) ||
    (needsHeaderMedia && header.assetId !== (campaign.headerMediaAssetId ?? null)) ||
    (needsHeaderText && headerText.trim() !== (campaign.headerText ?? ''));

  const blockers = campaignBlockers(
    map, needed,
    Boolean(campaign.segmentId ?? campaign.retargetOfCampaignId),
    template?.status ?? campaign.templateStatus,
  );
  if (needsHeaderMedia && !header.assetId) blockers.push(`Attach the ${headerFormat.toLowerCase()} this template's header shows.`);
  if (needsHeaderText && !headerText.trim()) blockers.push('Fill in the header value.');

  const attach = async (file: File) => {
    setUploading(true);
    setError(null);
    try {
      const uploaded = await inboxApi.uploadHeaderMedia(channelId, file);
      setHeader({ assetId: uploaded.mediaAssetId, fileName: uploaded.fileName });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'That file could not be uploaded.');
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  const unaffordable = estimate.data && !estimate.data.affordable && estimate.data.recipients > 0;

  return (
    <div className="pane-scroll h-full overflow-y-auto">
      <div className="mx-auto flex max-w-2xl flex-col gap-5 p-3 sm:p-5">
        {error && (
          <p role="alert" className="rounded-(--radius-field) border border-(--color-bad) px-3 py-2 text-small text-(--color-bad)">
            {error}
          </p>
        )}

        <label className="block">
          <span className="text-micro font-medium text-(--color-muted)">Campaign name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="mt-1 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-body"
          />
        </label>

        {/* Shown only when there is a choice. One number is the common case, and
            a select with one option is a question with no answer. */}
        {sendFrom.length > 1 && (
          <Section title="Sent from">
            <select
              value={channelId}
              onChange={(e) => {
                setChannelId(Number(e.target.value));
                // A Meta media id is minted on the number that uploaded it, so a
                // header file attached under the old number is cleared here and
                // the blockers ask for it again.
                if (needsHeaderMedia) setHeader({ assetId: null, fileName: null });
              }}
              aria-label="Number to send from"
              className="w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
            >
              {sendFrom.map((c) => (
                <option key={c.id} value={c.id}>{channelName(c)}</option>
              ))}
            </select>
            <p className="mt-1 text-micro text-(--color-muted)">
              Replies land in this number&rsquo;s inbox, and its quality rating carries the campaign.
            </p>
          </Section>
        )}

        <Section title="What goes out">
          {locked ? (
            <p className="text-body font-medium">{template?.name ?? campaign.templateName}</p>
          ) : (
          <select
            value={templateId}
            onChange={(e) => setTemplateId(Number(e.target.value))}
            className="w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
          >
            {(templates.data ?? []).map((t: Template) => (
              <option key={t.id} value={t.id} disabled={t.status?.toUpperCase() !== 'APPROVED'}>
                {t.name} {t.status?.toUpperCase() === 'APPROVED' ? '' : `· ${t.status?.toLowerCase()}`}
              </option>
            ))}
          </select>
          )}

          {needsHeaderMedia && (
            <div className="mt-2 flex flex-wrap items-center gap-2 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-3 py-2">
              <span className="text-small">
                <span className="font-medium">{headerFormat.charAt(0) + headerFormat.slice(1).toLowerCase()} header:</span>{' '}
                {header.fileName
                  ? <span className="text-(--color-ink-soft)">{header.fileName}</span>
                  : <span className="text-(--color-warn)">no file attached yet</span>}
              </span>
              <input
                ref={fileInput}
                type="file"
                accept={headerFormat === 'VIDEO' ? 'video/mp4,video/3gpp' : headerFormat === 'IMAGE' ? 'image/jpeg,image/png' : 'application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt'}
                onChange={(e) => { const f = e.target.files?.[0]; if (f) void attach(f); }}
                className="hidden"
                aria-label={`Attach the header ${headerFormat.toLowerCase()}`}
              />
              <button
                type="button"
                onClick={() => fileInput.current?.click()}
                disabled={uploading}
                className="tap ml-auto rounded-(--radius-field) border border-(--color-line) px-3 py-1.5 text-small disabled:opacity-40"
              >
                {uploading ? 'Uploading…' : header.assetId ? 'Replace file' : `Attach ${headerFormat.toLowerCase()}`}
              </button>
              <p className="basis-full text-micro text-(--color-muted)">
                {headerFormat === 'VIDEO' ? 'MP4, up to 16 MB.' : headerFormat === 'IMAGE' ? 'JPEG or PNG, up to 5 MB.' : 'PDF or Office file, up to 100 MB.'}
                {' '}Sent to everyone on the list as this template's header.
              </p>
            </div>
          )}

          {needsHeaderText && (
            <label className="mt-2 block">
              <span className="text-micro font-medium text-(--color-muted)">Header value {'{{1}}'}</span>
              <input
                value={headerText}
                onChange={(e) => setHeaderText(e.target.value)}
                maxLength={60}
                className="mt-1 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
              />
            </label>
          )}

          {template?.bodyText && (
            <p className="mt-2 rounded-(--radius-field) bg-(--color-sunken) px-3 py-2 text-small leading-relaxed whitespace-pre-wrap">
              {template.bodyText}
            </p>
          )}
        </Section>

        <Section title="Who it goes to">
          {locked ? (
            <p className="text-body font-medium">{locked.listName}</p>
          ) : campaign.retargetOfCampaignId ? (
            <p className="text-small text-(--color-ink-soft)">
              Everyone from an earlier campaign whose message was{' '}
              <strong>{outcomeLabel(campaign.retargetOutcome)}</strong>. People who
              have opted out since are never included.
            </p>
          ) : (
            <select
              value={segmentId ?? -1}
              onChange={(e) => setSegmentId(Number(e.target.value) < 0 ? null : Number(e.target.value))}
              className="w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
            >
              <option value={-1}>Choose a list…</option>
              {(segments.data ?? []).map((s: Segment) => (
                <option key={s.id} value={s.id}>
                  {s.name} {s.kind === 1 ? '· rules' : `· ${s.memberCount ?? 0}`}
                </option>
              ))}
            </select>
          )}

          {estimate.data && (
            <p className="mt-2 text-small text-(--color-muted)">
              About <strong className="text-(--color-ink)">{estimate.data.recipients.toLocaleString('en-IN')}</strong> people,
              roughly <strong className="text-(--color-ink)">₹{estimate.data.cost.toFixed(2)}</strong>.
              {' '}An estimate: the list is worked out again when it sends, so anyone
              who qualifies in the meantime is included.
            </p>
          )}
        </Section>

        {needed > 0 && (
          <Section title={`Filling in the ${needed === 1 ? 'value' : `${needed} values`}`}>
            <div className="flex flex-col gap-3">
              {map.map((binding, index) => (
                <BindingRow
                  key={index}
                  index={index}
                  binding={binding}
                  fields={fields.data ?? []}
                  onChange={(patch) => setMap((prev) => prev.map((b, i) => (i === index ? { ...b, ...patch } : b)))}
                />
              ))}
            </div>
            <p className="mt-2 text-micro leading-relaxed text-(--color-muted)">
              The fallback is used for anyone missing that value. It is not optional:
              WhatsApp refuses a message with a blank value, so a contact with no city
              would fail rather than simply read oddly.
            </p>
          </Section>
        )}

        {preview.data && (
          <Section title="How it reads">
            <p className="rounded-(--radius-panel) border border-(--color-line) bg-(--color-sunken) px-3 py-2.5 text-small leading-relaxed whitespace-pre-wrap">
              {preview.data.body}
            </p>
            <p className="mt-1.5 text-micro text-(--color-muted)">
              {preview.data.contact
                ? `As it will reach ${preview.data.contact.displayName ?? '+' + preview.data.contact.waId}, a real person on this list.`
                : 'Nobody is on this list yet.'}
              {preview.data.usedFallback > 0 &&
                ` ${preview.data.usedFallback} of the values fell back for them.`}
            </p>
          </Section>
        )}

        {blockers.length > 0 && (
          <ul className="flex flex-col gap-1 rounded-(--radius-panel) border border-(--color-warn) bg-(--color-warn)/8 p-3 text-small text-(--color-ink-soft)">
            {blockers.map((problem) => <li key={problem}>{problem}</li>)}
          </ul>
        )}

        {unaffordable && (
          <p role="alert" className="rounded-(--radius-panel) border border-(--color-bad) bg-(--color-bad)/8 p-3 text-small text-(--color-bad)">
            This needs ₹{estimate.data!.cost.toFixed(2)} and the wallet holds ₹{estimate.data!.balance.toFixed(2)}.
            Top up before starting — a campaign that runs out part way pauses, and the
            rest waits rather than being lost.
          </p>
        )}

        <Section title="When">
          <label className="flex items-center gap-2 text-small">
            <input
              type="radio"
              checked={!scheduled}
              onChange={() => setScheduled(false)}
              className="accent-(--color-brand)"
            />
            Send now
          </label>
          <label className="mt-1.5 flex items-center gap-2 text-small">
            <input
              type="radio"
              checked={scheduled}
              onChange={() => setScheduled(true)}
              className="accent-(--color-brand)"
            />
            Schedule
          </label>
          {scheduled && (
            <input
              type="datetime-local"
              value={when}
              min={istInputValue(1)}
              onChange={(e) => setWhen(e.target.value)}
              className="mt-2 w-full rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2.5 py-2 text-small"
            />
          )}
          <p className="mt-1.5 text-micro text-(--color-muted)">Times are IST.</p>
        </Section>

        <div className="flex flex-wrap items-center gap-2 border-t border-(--color-line) pt-4">
          <button
            type="button"
            disabled={!dirty || save.isPending}
            onClick={() => save.mutate()}
            className="tap rounded-(--radius-field) border border-(--color-line) px-3 py-2 text-small disabled:opacity-40"
          >
            {save.isPending ? 'Saving…' : saved && !dirty ? 'Saved' : 'Save draft'}
          </button>
          {saved && !dirty && <span className="text-small text-(--color-good)">Draft saved — you can send it now.</span>}

          <button
            type="button"
            disabled={dirty || blockers.length > 0 || launch.isPending || unaffordable}
            title={dirty ? 'Save your changes first' : undefined}
            onClick={() => launch.mutate()}
            className="tap ml-auto rounded-(--radius-field) bg-(--color-brand) px-4 py-2 text-small font-medium text-white disabled:opacity-40"
          >
            {launch.isPending
              ? 'Starting…'
              : scheduled
                ? 'Schedule'
                : `Send to ${estimate.data?.recipients?.toLocaleString('en-IN') ?? '…'} people`}
          </button>
        </div>

        {dirty && (
          <p className="text-micro text-(--color-muted)">
            Save first. The audience and the preview are worked out from what is
            stored, so sending an unsaved change would send the previous one.
          </p>
        )}
      </div>
    </div>
  );
}

function BindingRow({ index, binding, fields, onChange }: {
  index: number;
  binding: VariableBinding;
  fields: ContactField[];
  onChange: (patch: Partial<VariableBinding>) => void;
}) {
  const shell = 'min-w-0 rounded-(--radius-field) border border-(--color-line) bg-(--color-paper) px-2 py-1.5 text-small';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="w-16 shrink-0 text-micro font-medium text-(--color-muted)">{`{{${index + 1}}}`}</span>

      <select
        value={binding.source}
        onChange={(e) => {
          const source = e.target.value as VariableBinding['source'];
          // The old clause's key and value belong to the old source; carrying them
          // over produces a map the server refuses, and the refusal reads as a bug.
          onChange({
            source,
            key: source === 'field' ? (fields[0]?.fieldKey ?? '') : null,
            value: source === 'literal' ? '' : null,
          });
        }}
        className={`${shell} flex-1`}
      >
        <option value="name">Their name</option>
        {fields.length > 0 && <option value="field">A contact field</option>}
        <option value="literal">The same for everyone</option>
      </select>

      {binding.source === 'field' && (
        <select
          value={binding.key ?? ''}
          onChange={(e) => onChange({ key: e.target.value })}
          className={`${shell} flex-1`}
        >
          {fields.map((f) => <option key={f.fieldKey} value={f.fieldKey}>{f.label}</option>)}
        </select>
      )}

      {binding.source === 'literal' && (
        <input
          value={binding.value ?? ''}
          onChange={(e) => onChange({ value: e.target.value })}
          placeholder="Value"
          className={`${shell} flex-1`}
        />
      )}

      <input
        value={binding.fallback ?? ''}
        onChange={(e) => onChange({ fallback: e.target.value })}
        placeholder="Fallback"
        aria-label={`Fallback for value ${index + 1}`}
        className={`${shell} w-28 shrink-0 ${binding.fallback?.trim() ? '' : 'border-(--color-warn)'}`}
      />
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-small font-semibold">{title}</h3>
      {children}
    </section>
  );
}

export function outcomeLabel(outcome: string | null | undefined): string {
  switch (outcome) {
    case 'delivered_unread': return 'delivered but not read';
    case 'read_unclicked': return 'read but not acted on';
    case 'clicked': return 'tapped a button';
    case 'failed': return 'not delivered';
    default: return 'from an earlier campaign';
  }
}
